import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Search, Pencil, ChevronDown, ChevronRight } from "lucide-react";
import { ContactFormDialog } from "@/components/inquiries/ContactFormDialog";
import { CONTACT_TYPES, Pill, KIND_STYLES } from "@/lib/inquiry";
import { fmtDate } from "@/lib/format";

const History = ({ contactId }) => {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/inquiries?contact_id=${contactId}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [contactId]);
  if (!rows) return <div className="text-xs text-slate-400 px-4 py-2">Loading…</div>;
  if (!rows.length) return <div className="text-xs text-slate-500 px-4 py-2" data-testid={`contact-history-empty-${contactId}`}>No inquiries yet.</div>;
  return (
    <div className="px-4 pb-3" data-testid={`contact-history-${contactId}`}>
      {rows.map((r) => (
        <button type="button" key={r.id} onClick={() => nav(`/inquiries/${r.id}`)} className="w-full flex items-center gap-3 text-left text-xs py-1.5 hover:text-blue-700">
          <span className="mono text-blue-700 w-28">{r.number}</span><span className="flex-1">{r.subject}</span>
          <Pill className={KIND_STYLES[r.status_kind]}>{r.status_label}</Pill><span className="w-24 text-right">{fmtDate(r.inquiry_date)}</span>
        </button>
      ))}
    </div>
  );
};

export default function Contacts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (type) p.set("type", type);
    api.get(`/contacts?${p}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [q, type]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Inquiries</div><h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Contacts</h1></div>
        <Button data-testid="contact-add-btn" onClick={() => setEdit({})} className="bg-blue-700 hover:bg-blue-800 text-white gap-2"><Plus className="w-4 h-4" />New contact</Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="contact-list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, organisation, email, phone" className="h-9 pl-8 w-72" /></div>
        {["", ...CONTACT_TYPES].map((t) => (
          <button type="button" key={t || "all"} data-testid={`contact-type-filter-${t || "all"}`} onClick={() => setType(t)}
            className={`px-3 h-9 text-xs rounded-md border capitalize ${type === t ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700 bg-white"}`}>{t || "All"}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {rows.length === 0 && <div className="text-sm text-slate-500 p-6 text-center" data-testid="contacts-empty">No contacts yet.</div>}
        {rows.map((c) => (
          <div key={c.id} data-testid={`contact-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <button type="button" data-testid={`contact-expand-${c.id}`} onClick={() => setOpen(open === c.id ? null : c.id)} className="text-slate-400">{open === c.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
              <div className="flex-1 min-w-[200px]"><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-slate-500">{[c.designation, c.organisation].filter(Boolean).join(" · ") || "—"}</div></div>
              <div className="text-xs text-slate-600 w-56 truncate">{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}</div>
              <Pill className="bg-slate-50 border-slate-200 text-slate-600 capitalize">{c.type}</Pill>
              <span className="text-xs text-slate-500 w-24 text-right">{c.inquiry_count} {c.inquiry_count === 1 ? "inquiry" : "inquiries"}</span>
              <Button size="icon" variant="ghost" data-testid={`contact-edit-${c.id}`} onClick={() => setEdit(c)} className="h-8 w-8"><Pencil className="w-4 h-4" /></Button>
            </div>
            {open === c.id && <History contactId={c.id} />}
          </div>
        ))}
      </div>
      <ContactFormDialog open={!!edit} contact={edit?.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
